// PWA, page side (2026-11-02): registers the service worker (src/pwa/sw.js -> dist/sw.js) and hands updates over
// without ever interrupting a run. Docs/QA/PWA.md has the whole flow.
//
//  - Registers only in production builds on a secure context. Failure (e.g. the LAN host's self-signed certificate is
//    not trusted on this device, or plain HTTP) is silent: the game runs exactly as before, without offline play.
//  - A new worker never activates by itself. It takes over (skipWaiting + clients.claim inside the worker) only when
//    the page sends SKIP_WAITING, which happens (a) when the player presses UPDATE AVAILABLE on the title, or (b) on the
//    title screen when the waiting worker is for the very build this page already runs (the swap is invisible), or
//    (c) at boot when the waiting worker is newer than the page (one reload during the loading screen).
//  - ?nosw unregisters the worker and drops its caches (kill switch / debugging); ?sw forces registration in dev.

const BUILD_ID: string = (import.meta as { env?: { VITE_BUILD_ID?: string } }).env?.VITE_BUILD_ID ?? 'dev';
const PROD = !!(import.meta as { env?: { PROD?: boolean } }).env?.PROD;
const BOOT_KEY = 'sla.sw.bootReload';

export interface PwaStatus {
  /** off = dev server; unsupported / insecure / disabled (?nosw) / failed = no worker, game unaffected */
  sw: 'off' | 'unsupported' | 'insecure' | 'disabled' | 'registering' | 'ready' | 'failed';
  reason?: string;
  /** this page is controlled by a worker (offline-capable after its install finished) */
  controlled: boolean;
  /** build id of the controlling worker */
  version?: string;
  build: string;
  updateReady: boolean;
  standalone: boolean;
}
export interface Pwa { status: PwaStatus; applyUpdate(): void; /** version.json says a new deploy is live: fetch its worker now */ checkForUpdate(): void }

export function isStandalone(): boolean {
  const mm = (q: string) => typeof matchMedia === 'function' && matchMedia(q).matches;
  return mm('(display-mode: fullscreen)') || mm('(display-mode: standalone)') || mm('(display-mode: minimal-ui)')
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** the old update path (before the worker): reload with a cache-busting query */
function reloadFresh() { const u = new URL(location.href); u.searchParams.set('v', String(Date.now())); location.replace(u.toString()); }

/** ask a worker which build it caches (null if it doesn't answer quickly) */
function askVersion(w: ServiceWorker | null): Promise<string | null> {
  if (!w) return Promise.resolve(null);
  return new Promise((res) => {
    const ch = new MessageChannel();
    const t = setTimeout(() => res(null), 1500);
    ch.port1.onmessage = (e) => { clearTimeout(t); res(typeof e.data?.v === 'string' ? e.data.v : null); };
    try { w.postMessage({ type: 'VERSION' }, [ch.port2]); } catch { clearTimeout(t); res(null); }
  });
}

export function initPwa(o: { isSafe(): boolean; onUpdateReady(): void }): Pwa {
  const status: PwaStatus = { sw: 'registering', controlled: false, build: BUILD_ID, updateReady: false, standalone: isStandalone() };
  let reg: ServiceWorkerRegistration | undefined, reloading = false, wantReload = false;
  const api: Pwa = {
    status,
    applyUpdate() {
      // UPDATE AVAILABLE pressed (title only): swap in the waiting worker, then reload. Without one, a plain reload is
      // enough: the running worker sees the newer version.json on that navigation and serves the network build.
      const w = reg?.waiting;
      if (w && navigator.serviceWorker?.controller) { wantReload = true; w.postMessage({ type: 'SKIP_WAITING' }); setTimeout(reloadFresh, 4000); }
      else reloadFresh();
    },
    checkForUpdate() { void reg?.update().catch(() => {}); },
  };
  (window as unknown as { __pwa: Pwa }).__pwa = api;
  const params = new URLSearchParams(location.search);
  const sw = navigator.serviceWorker;
  if (!sw) { status.sw = 'unsupported'; return api; }
  if (params.has('nosw')) {
    status.sw = 'disabled';
    void sw.getRegistrations().then((rs) => Promise.all(rs.map((r) => r.unregister()))).catch(() => {});
    void caches?.keys().then((ks) => Promise.all(ks.filter((k) => k.startsWith('sla-')).map((k) => caches.delete(k)))).catch(() => {});
    return api;
  }
  if (!PROD && !params.has('sw')) { status.sw = 'off'; return api; }
  if (!window.isSecureContext) { status.sw = 'insecure'; return api; }

  sw.addEventListener('controllerchange', () => {
    status.controlled = true;
    void askVersion(sw.controller).then((v) => { if (v) status.version = v; });
    if (wantReload && !reloading) { reloading = true; reloadFresh(); }
  });
  const activate = (w: ServiceWorker, reload: boolean) => { wantReload ||= reload; w.postMessage({ type: 'SKIP_WAITING' }); };
  /** run fn once the player is on the title screen (never mid-run) */
  const whenSafe = (fn: () => void) => { if (o.isSafe()) fn(); else { const t = setInterval(() => { if (o.isSafe()) { clearInterval(t); fn(); } }, 3000); } };
  const announce = () => { if (!status.updateReady) { status.updateReady = true; o.onUpdateReady(); } };

  /** a worker finished installing while another one controls this page */
  const onWaiting = async (w: ServiceWorker, atBoot: boolean) => {
    if (!sw.controller) return; // first install: it activates by itself, nothing to swap
    const v = await askVersion(w);
    if (v && v === BUILD_ID) { whenSafe(() => activate(w, false)); return; } // same build as this page: invisible swap
    let recent = false;
    try { recent = Date.now() - Number(sessionStorage.getItem(BOOT_KEY) || 0) < 30000; } catch { /* storage blocked */ }
    if (atBoot && o.isSafe() && !recent) { // still loading: take the new build now (one reload, never a loop)
      try { sessionStorage.setItem(BOOT_KEY, String(Date.now())); } catch { /* ignore */ }
      activate(w, true);
      return;
    }
    announce(); // the title shows UPDATE AVAILABLE; the player decides when
  };
  const track = (w: ServiceWorker | null) => {
    if (!w) return;
    w.addEventListener('statechange', () => { if (w.state === 'installed' && reg?.waiting === w) void onWaiting(w, false); });
  };

  sw.register('./sw.js', { scope: './', updateViaCache: 'none' }).then(async (r) => {
    reg = r; status.sw = 'ready'; status.controlled = !!sw.controller;
    if (sw.controller) status.version = (await askVersion(sw.controller)) ?? undefined;
    if (r.waiting) void onWaiting(r.waiting, true);
    track(r.installing);
    r.addEventListener('updatefound', () => track(r.installing));
    if (status.standalone) void navigator.storage?.persist?.().catch(() => false); // installed app: keep the offline copy
  }).catch((e: unknown) => {
    // e.g. "An SSL certificate error occurred when fetching the script" on an untrusted self-signed host: no offline
    // play there, nothing else changes (Docs/QA/PWA.md: trust the certificate or use a real one)
    status.sw = 'failed'; status.reason = e instanceof Error ? e.message : String(e);
    console.info('[pwa] service worker not registered:', status.reason);
  });
  return api;
}
